import {
    Box,
    Chip,
    Container, Divider, IconButton, Typography,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Delete, Description, Star,
} from '@mui/icons-material';
import { LoggedInUser, UserProfileViewModel, UserSettingsViewModel } from '../../../types/auth.types';
import Button from '../foundation/Button';
import { ShadowBGContainer } from '../foundation/Containers';
import CustomBR from '../foundation/CustomBR';
import DateFormat from '../foundation/DateFormat';
import Page from '../layout/base.page';
import { deleteCV, setDefaultCV, uploadUserCV } from '../../api/user.api';
import UserNotification, { UserNotificationProps } from '../components/UserNotification';
import Tooltip from '../foundation/Tooltip';
import Anchor from '../foundation/Anchor';
import Alert from '../foundation/Alert';
import DialogWithHeader from '../foundation/DialogWithHeader';
import CvUploadDropZone from '../components/CvUploadDropZone';

export type UserProfilePageProps = {
    url: string
    user: LoggedInUser
    userProfile: UserProfileViewModel
    userSettings: UserSettingsViewModel
}

const MAX_CVS = 3;

const CVItemCard = styled('div')<{ $isDefault?: boolean }>`
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-radius: 10px;
    gap: 0.75rem;
    background: ${({ $isDefault }) => ($isDefault ? '#e3f2fd40' : 'transparent')};
    border: 1px solid ${({ $isDefault }) => ($isDefault ? '#1976d230' : 'transparent')};
    transition: background 0.2s;

    &:hover {
        background: ${({ $isDefault }) => ($isDefault ? '#e3f2fd60' : '#f5f5f5')};
    }

    ${props => props.theme.breakpoints.down('sm')} {
        flex-wrap: wrap;
        gap: 0.5rem;
        padding: 0.75rem;
    }
`;

const CVItemInfo = styled('div')`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;

    ${props => props.theme.breakpoints.down('sm')} {
        width: 100%;
    }
`;

const CVFileName = styled('div')`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CVItemActions = styled('div')`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-inline-start: auto;
    flex-shrink: 0;

    ${props => props.theme.breakpoints.down('sm')} {
        width: 100%;
        justify-content: flex-end;
        padding-top: 0.25rem;
        border-top: 1px solid #f0f0f0;
    }
`;

const EmptyStateBox = styled('div')`
    text-align: center;
    padding: 2rem 1rem;
`;

const UserProfilePage = (props: UserProfilePageProps) => {
    const [notification, setNotification] = useState<UserNotificationProps>(null);
    const [userProfile, setUserProfile] = useState<UserProfileViewModel>(props.userProfile);
    const [isUploading, setIsUploading] = useState(false);
    const [isOperating, setIsOperating] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ cvid: number; name: string } | null>(null);

    const cvCount = userProfile.cvs?.length || 0;
    const atMaxCVs = cvCount >= MAX_CVS;
    const canInteract = !isUploading && !isOperating;

    const notify = (message: string, severity: UserNotificationProps['severity']) => {
        setNotification({ id: Math.random(), message, severity });
    };

    const processFile = async (file: File) => {
        if (!file || isUploading || atMaxCVs) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const newCV = await uploadUserCV({ userid: props.user.userid }, formData);
            setUserProfile(prev => ({
                ...prev,
                cvs: [newCV.data, ...prev.cvs].map(cv => ({
                    ...cv,
                    default: cv.cvid === newCV.data.cvid ? true : null,
                })),
            }));
            notify('קורות החיים הועלו למערכת בהצלחה', 'success');
        } catch (error) {
            Sentry.captureException(error);
            notify(error.message || 'אירעה שגיאה בהעלאת הקובץ', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const setCVAsDefaultHandler = async (cvid: number) => {
        if (!canInteract) return;
        setIsOperating(true);
        try {
            await setDefaultCV({ userid: props.user.userid, cvid }, { default: true });
            setUserProfile(prev => ({
                ...prev,
                cvs: prev.cvs?.map(cv => ({
                    ...cv,
                    default: cv.cvid === cvid ? true : null,
                })),
            }));
            notify('קורות החיים הוגדרו כברירת מחדל בהצלחה', 'success');
        } catch (error) {
            Sentry.captureException(error);
            notify(error.message || 'אירעה שגיאה בהגדרת ברירת המחדל', 'error');
        } finally {
            setIsOperating(false);
        }
    };

    const confirmDelete = (cvid: number, name: string) => {
        if (!canInteract) return;
        setDeleteTarget({ cvid, name });
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        const { cvid } = deleteTarget;
        setDeleteTarget(null);
        setIsOperating(true);
        try {
            await deleteCV({ userid: props.user.userid, cvid });
            setUserProfile(prev => ({
                ...prev,
                cvs: prev.cvs?.filter(cv => cv.cvid !== cvid),
            }));
            notify('קורות החיים נמחקו בהצלחה', 'success');
        } catch (error) {
            Sentry.captureException(error);
            notify(error.message || 'אירעה שגיאה במחיקת קורות החיים', 'error');
        } finally {
            setIsOperating(false);
        }
    };

    return <Page
        url={props.url}
        user={props.user}
        pageHeaderTitle={'Mploy - ניהול קו"ח'}>
        <Container maxWidth="md">
            <CustomBR />
            {notification && <><UserNotification blink {...notification} /><CustomBR /></>}
            <ShadowBGContainer>
                <Typography color="secondary" fontWeight="bold" variant="h4">ניהול קורות החיים שלי</Typography>
                <CustomBR />

                <CvUploadDropZone
                    disabled={atMaxCVs || isUploading}
                    isUploading={isUploading}
                    disabledLabel={`הגעת למקסימום ${MAX_CVS} קבצים`}
                    counterText={cvCount > 0 ? `${cvCount} מתוך ${MAX_CVS} קבצים` : undefined}
                    onFileSelected={processFile}
                    onValidationError={message => notify(message, 'error')}
                />

                <CustomBR />
                <Typography color="secondary" fontWeight="bold" variant="h5">קורות החיים במערכת</Typography>
                <CustomBR h={10} />

                {cvCount === 0
                    ? <EmptyStateBox>
                        <Description sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                        <Typography variant="body1" color="text.secondary">עדיין לא הועלו קורות חיים</Typography>
                        <Typography variant="body2" color="text.secondary">
                            העלה קובץ כדי להתחיל להגיש מועמדות למשרות
                        </Typography>
                    </EmptyStateBox>
                    : <div>
                        {userProfile.cvs.map((cv, index) => <div key={cv.cvid}>
                            {index > 0 && <Divider sx={{ my: 0.5 }} />}
                            <CVItemCard $isDefault={!!cv.default}>
                                <CVItemInfo>
                                    <Description color="secondary" />
                                    <CVFileName>
                                        <Anchor blank href={`/user/downloadCV/${cv.cvid}`} color="secondary" fontWeight="bold">
                                            {cv.originalName}
                                        </Anchor>
                                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.25}>
                                            <DateFormat showTime date={cv.createdAt} />
                                            {cv.default && <Chip
                                                icon={<Star sx={{ fontSize: 16 }} />}
                                                label="ברירת מחדל"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ height: 24, fontSize: '0.75rem' }}
                                            />}
                                        </Box>
                                    </CVFileName>
                                </CVItemInfo>
                                <CVItemActions>
                                    {!cv.default && <Tooltip title="הגדר כברירת מחדל">
                                        <span>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                disabled={!canInteract}
                                                onClick={() => setCVAsDefaultHandler(cv.cvid)}
                                                sx={{ whiteSpace: 'nowrap' }}
                                            >
                                                הגדר כברירת מחדל
                                            </Button>
                                        </span>
                                    </Tooltip>}
                                    {cv.default && cvCount > 1
                                        ? <Tooltip title="לא ניתן למחוק קו״ח שהוגדרו כברירת מחדל. הגדר קובץ אחר כברירת מחדל תחילה.">
                                            <span>
                                                <IconButton disabled>
                                                    <Delete />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        : <Tooltip title="מחיקת קורות חיים">
                                            <span>
                                                <IconButton
                                                    disabled={!canInteract}
                                                    onClick={() => confirmDelete(cv.cvid, cv.originalName)}
                                                >
                                                    <Delete color={canInteract ? 'error' : 'inherit'} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    }
                                </CVItemActions>
                            </CVItemCard>
                        </div>)}
                    </div>
                }

                {props.userSettings.showToEmployers && <>
                    <CustomBR />
                    <Alert severity="info">
                        <>
                            הינך מוצג בפני מעסיקים פוטנציאלים.&nbsp;
                            <Anchor href="/user/settings">הגדרות משתמש</Anchor>
                        </>
                    </Alert>
                </>}

                <CustomBR />
            </ShadowBGContainer>
        </Container>
        <CustomBR />

        <DialogWithHeader
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            title="מחיקת קורות חיים"
            firstActionText="ביטול"
            onFirstActionClick={() => setDeleteTarget(null)}
            secondActionText="מחק"
            onSecondActionClick={handleDelete}
        >
            <Typography variant="body1" textAlign="center" py={1}>
                <>האם למחוק את הקובץ <strong>{deleteTarget?.name}</strong>?
                    <br />
                    פעולה זו אינה ניתנת לביטול.</>
            </Typography>
        </DialogWithHeader>
    </Page>;
};

export default UserProfilePage;
